import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import './App.css';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard
import Dashboard from './pages/Dashboard';

// Experiments
import ExperimentsPage from './pages/experiments/ExperimentsPage';
import ExperimentForm from './pages/experiments/ExperimentForm';
import ExperimentDetail from './pages/experiments/ExperimentDetail';

// Protocols
import ProtocolsPage from './pages/protocols/ProtocolsPage';
import ProtocolForm from './pages/protocols/ProtocolForm';
import BenchMode from './pages/protocols/BenchMode';

// Plasmids
import { PlasmidsPage, PlasmidForm, PlasmidLineage } from './pages/plasmids/PlasmidsPage';

// Strains
import { StrainsPage, StrainForm } from './pages/strains/StrainsPage';

// Samples
import { SamplesPage, SampleForm } from './pages/samples/SamplesPage';

// Inventory
import InventoryPage from './pages/inventory/InventoryPage';
import InventoryForm from './pages/inventory/InventoryForm';

// Sequences
import SequencesPage from './pages/sequences/SequencesPage';

// Data Analysis
import DataAnalysisPage from './pages/analysis/DataAnalysisPage';

// Figure Maker
import FigureMakerPage from './pages/figures/FigureMakerPage';

// Search
import SearchPage from './pages/search/SearchPage';

// Settings
import SettingsPage from './pages/settings/SettingsPage';

// User Management
import UserManagementPage from './pages/users/UserManagementPage';

// Calendar
import CalendarPage from './pages/calendar/CalendarPage';

// My Files
import MyFilesPage from './pages/files/MyFilesPage';

// Research
import ResearchPage from './pages/research/ResearchPage';

// Chat
import ChatPage from './pages/chat/ChatPage';

// New Phase 1+2 Features
import FailureDashboardPage from './pages/failures/FailureDashboardPage';
import AIAssistantPage from './pages/ai/AIAssistantPage';
import LineagePage from './pages/lineage/LineagePage';
import CrisprDesignerPage from './pages/tools/CrisprDesignerPage';
import SmartInventoryPage from './pages/inventory/SmartInventoryPage';
import PaperToProtocolPage from './pages/tools/PaperToProtocolPage';
import ProjectsPage from './pages/projects/ProjectsPage';
import ProjectDetailPage from './pages/projects/ProjectDetailPage';
import EquipmentPage from './pages/equipment/EquipmentPage';
import LN2StoragePage from './pages/equipment/LN2StoragePage';
import Freezer80Page from './pages/equipment/Freezer80Page';
import SampleInventoryPage from './pages/inventory/SampleInventoryPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Experiments */}
      <Route path="/experiments" element={<ProtectedRoute><ExperimentsPage /></ProtectedRoute>} />
      <Route path="/experiments/new" element={<ProtectedRoute><ExperimentForm /></ProtectedRoute>} />
      <Route path="/experiments/:id" element={<ProtectedRoute><ExperimentDetail /></ProtectedRoute>} />
      <Route path="/experiments/:id/edit" element={<ProtectedRoute><ExperimentForm isEdit /></ProtectedRoute>} />

      {/* Protocols */}
      <Route path="/protocols" element={<ProtectedRoute><ProtocolsPage /></ProtectedRoute>} />
      <Route path="/protocols/new" element={<ProtectedRoute><ProtocolForm /></ProtectedRoute>} />
      <Route path="/protocols/:id" element={<ProtectedRoute><ProtocolForm isEdit /></ProtectedRoute>} />
      <Route path="/protocols/:id/edit" element={<ProtectedRoute><ProtocolForm isEdit /></ProtectedRoute>} />
      <Route path="/protocols/:id/bench" element={<ProtectedRoute><BenchMode /></ProtectedRoute>} />

      {/* Plasmids */}
      <Route path="/plasmids" element={<ProtectedRoute><PlasmidsPage /></ProtectedRoute>} />
      <Route path="/plasmids/new" element={<ProtectedRoute><PlasmidForm /></ProtectedRoute>} />
      <Route path="/plasmids/:id" element={<ProtectedRoute><PlasmidForm isEdit /></ProtectedRoute>} />
      <Route path="/plasmids/:id/edit" element={<ProtectedRoute><PlasmidForm isEdit /></ProtectedRoute>} />
      <Route path="/plasmids/:id/lineage" element={<ProtectedRoute><PlasmidLineage /></ProtectedRoute>} />

      {/* Strains */}
      <Route path="/strains" element={<ProtectedRoute><StrainsPage /></ProtectedRoute>} />
      <Route path="/strains/new" element={<ProtectedRoute><StrainForm /></ProtectedRoute>} />
      <Route path="/strains/:id" element={<ProtectedRoute><StrainForm isEdit /></ProtectedRoute>} />
      <Route path="/strains/:id/edit" element={<ProtectedRoute><StrainForm isEdit /></ProtectedRoute>} />

      {/* Samples */}
      <Route path="/samples" element={<ProtectedRoute><SamplesPage /></ProtectedRoute>} />
      <Route path="/samples/new" element={<ProtectedRoute><SampleForm /></ProtectedRoute>} />
      <Route path="/samples/:id" element={<ProtectedRoute><SampleForm isEdit /></ProtectedRoute>} />
      <Route path="/samples/:id/edit" element={<ProtectedRoute><SampleForm isEdit /></ProtectedRoute>} />

      {/* Inventory */}
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/inventory/new" element={<ProtectedRoute><InventoryForm /></ProtectedRoute>} />
      <Route path="/inventory/:id" element={<ProtectedRoute><InventoryForm isEdit /></ProtectedRoute>} />
      <Route path="/inventory/:id/edit" element={<ProtectedRoute><InventoryForm isEdit /></ProtectedRoute>} />

      {/* Sequences */}
      <Route path="/sequences" element={<ProtectedRoute><SequencesPage /></ProtectedRoute>} />
      <Route path="/sequences/new" element={<ProtectedRoute><SequencesPage /></ProtectedRoute>} />

      {/* Data Analysis */}
      <Route path="/analysis" element={<ProtectedRoute><DataAnalysisPage /></ProtectedRoute>} />

      {/* Figure Maker */}
      <Route path="/figures" element={<ProtectedRoute><FigureMakerPage /></ProtectedRoute>} />

      {/* Search */}
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />

      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* User Management */}
      <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />

      {/* Calendar */}
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />

      {/* My Files */}
      <Route path="/my-files" element={<ProtectedRoute><MyFilesPage /></ProtectedRoute>} />

      {/* Research */}
      <Route path="/research" element={<ProtectedRoute><ResearchPage /></ProtectedRoute>} />

      {/* Chat */}
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />

      {/* Phase 1+2 Features */}
      <Route path="/failures" element={<ProtectedRoute><FailureDashboardPage /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
      <Route path="/lineage" element={<ProtectedRoute><LineagePage /></ProtectedRoute>} />
      <Route path="/crispr-designer" element={<ProtectedRoute><CrisprDesignerPage /></ProtectedRoute>} />
      <Route path="/smart-inventory" element={<ProtectedRoute><SmartInventoryPage /></ProtectedRoute>} />
      <Route path="/paper-to-protocol" element={<ProtectedRoute><PaperToProtocolPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />

      {/* Equipment */}
      <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
      <Route path="/ln2-storage" element={<ProtectedRoute><LN2StoragePage /></ProtectedRoute>} />
      <Route path="/freezer80" element={<ProtectedRoute><Freezer80Page /></ProtectedRoute>} />
      <Route path="/sample-inventory" element={<ProtectedRoute><SampleInventoryPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
